import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListSubheader,
} from "@mui/material";
import { MoreVert, Delete, Add } from "@mui/icons-material";
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import {
  allStatus,
  convertFirstUpper,
  statusType,
} from "../../store/slices/constants/staticContents";
import { MockLevelTypes } from "../../store/slices/detailedMaster/detailedMasterSlice";

interface ActionsProps {
  onDelete: () => void;
  onCut: () => void;
  onStatus: (data: any) => void;
  row: any;
  type?: MockLevelTypes;
  disabled?: boolean;
  anchorEls?: any;
  setAnchorEls?: any;
  bulkaddQuestion?: any;
}

const Actions: React.FC<ActionsProps> = ({
  onDelete,
  onCut,
  onStatus,
  row,
  type,
  disabled = false,
  anchorEls,
  setAnchorEls,
  bulkaddQuestion,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  useEffect(() => {
    if (anchorEls?.e) {
      setAnchorEl(anchorEls.e);
    }
  }, [anchorEls?.e]);
  const handleClose = () => {
    setAnchorEl(null);
    setAnchorEls?.(null);
  };

  // 🔹 Generates list of status-based actions
  const getStatusButtons = (list: statusType[]) => {
    return list.map((item: statusType) => {
      const label =
        typeof item.actionLabel === "function"
          ? item.actionLabel(item.label)
          : item.actionLabel;
      return {
        label,
        onClick: () => onStatus(item.label),
      };
    });
  };

  // 🔹 Builds context-specific action array
  const getDynamicActions = (): any[] => {
    if (!row?.status) return [];

    const status = row.status.toLowerCase();
    const find = (label: string) =>
      getStatusButtons(allStatus.filter((s) => s.label === label))[0];

    const actions: any[] = [];

    switch (status) {
      case "draft":
        actions.push(find("ready"));
        break;
      case "ready":
        actions.push(find("approved"), find("rejected"), find("draft"));
        break;
      case "approved":
        actions.push(find("published"), find("rejected"));
        break;
      case "published":
        actions.push(find("archived"), find("unpublished"));
        break;
      case "archived":
        // actions.push(find("draft"));
        actions.push(find("unpublished"));
        break;
      case "rejected":
        actions.push(find("draft"));
        break;
      case "unpublished":
        // actions.push(find("draft"));
        actions.push(find("archived"), find("published"));
        break;
      default:
        break;
    }

    return actions.filter(Boolean);
  };

  const staticMenuItems = [
    { label: "Copy", icon: <ContentCopyOutlinedIcon fontSize="small" />, onClick: onCut },
  ];

  const menuItems = bulkaddQuestion
    ? [
        {
          label: "Import Questions from DOCX",
          icon: <Add fontSize="small" />,
          onClick: bulkaddQuestion,
        },
        ...staticMenuItems,
        ...getDynamicActions(),
      ]
    : [...staticMenuItems, ...getDynamicActions()];

  return (
    <Box>
      {!disabled && (
        <IconButton
          onClick={handleClick}
          sx={{
            p: "4px",
            borderRadius: "6px",
            transition: "0.2s",
            "&:hover": {
              backgroundColor: "rgba(25,118,210,0.08)",
            },
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        anchorReference={
          anchorEls?.axis && anchorEls.axis.mouseY
            ? "anchorPosition"
            : undefined
        }
        anchorPosition={
          anchorEls?.axis && anchorEls.axis.mouseY
            ? { top: anchorEls.axis.mouseY, left: anchorEls.axis.mouseX }
            : undefined
        }
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 200,
            borderRadius: 2,
            boxShadow: 3,
            py: 0.5,
          },
        }}
      >
        <ListSubheader>
          {`Action for ${convertFirstUpper(
            row?.name || "Selected"
          )} ${convertFirstUpper((type ?? "").toLowerCase() || "")}${
            row?.name ? "" : "s"
          }`}
        </ListSubheader>

        <Divider sx={{ my: 0.5 }} />

        {menuItems.map((item, index) => (
          <Box key={item.label}>
            <MenuItem
              onClick={() => {
                item.onClick?.();
                handleClose();
              }}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 1,
                px: 2,
                borderRadius: 1,
                "&:hover": { backgroundColor: "rgba(25,118,210,0.08)" },
              }}
            >
              <Typography fontSize="0.9rem">{item.label}</Typography>
              {item.icon}
            </MenuItem>
            {index < menuItems.length - 1 && <Divider />}
          </Box>
        ))}
      </Menu>
    </Box>
  );
};

export default Actions;
